/* qeslint-disable */
// q@ts-nocheck

import { BillboardCollection, Cartesian3, createGuid, destroyObject, Event, Label } from "cesium";

import {
    calculateAreaOfPolygon,
    DrawingMode,
    MeasureUnits,
    MouseEvent,
    PolygonDrawingHandler,
    PrimitiveSettings
} from "../../../core";
import { newCartesian3ArrayFromArray } from "../util";
import { EditablePolygon } from "../EditablePolygon";

import { MeasurementTool, MeasurementToolConstructorOptions, PositionData } from "./MeasurementTool";
import { calculateMostTopPosition } from "./common";
import { AreaMeasurement } from "./AreaMeasurement";

/**
 * Creates a polygonal area measurement.
 */
export class AreaMeasurementTool extends MeasurementTool {
    private readonly _polygonDrawingHandler: PolygonDrawingHandler;
    private readonly _billboardCollection: BillboardCollection;
    private _area: number;
    private _label: Label;

    private _removeEvent: Event.RemoveCallback;

    constructor(options: MeasurementToolConstructorOptions) {
        super(options);

        this._billboardCollection = new BillboardCollection();
        this.scene.primitives.add(this._billboardCollection);

        const drawingOptions = {
            scene: this.scene,
            primitives: options.primitives,
            pointOptions: PrimitiveSettings.getPointOptions(),
            polylineOptions: PrimitiveSettings.getPolylineOptions({ allowPicking: false }),
            polygonOptions: PrimitiveSettings.getPolygonOptions({ allowPicking: false }),
            showVertices: false
        };

        this._polygonDrawingHandler = new PolygonDrawingHandler(drawingOptions);

        this._label = this._labelCollection.add(PrimitiveSettings.getLabelOptions());

        this._area = 0;

        this._removeEvent = this._scene.preRender.addEventListener(() => {
            this.updateLabel();
        });
    }

    activate(): void {
        super.activate();

        this._polygonDrawingHandler.reset();
    }

    deactivate(): void {
        super.deactivate();

        this._polygonDrawingHandler.reset();
        this._label.show = false;
    }

    get area() {
        return this._area;
    }

    get label() {
        return this._label;
    }

    updateArea(positions: Cartesian3[]) {
        const area = calculateAreaOfPolygon(positions);

        this._area = area;
        this._label.text = MeasureUnits.areaToString(area, this._selectedUnits.areaUnits, this._selectedLocale, 3);
    }

    updateLabel() {
        const positions = this._polygonDrawingHandler.positions;

        if (positions.length < 2) {
            return;
        }

        const scene = this._scene;
        const newPos = calculateMostTopPosition(scene, positions, new Cartesian3());

        this._label.position = newPos;
    }

    canvasDoubleClickEvent(): void {
        this.finishDrawing();
    }

    finishDrawing(): void {
        this._polygonDrawingHandler.handleDoubleClick();

        this.saveCurrentMeasurementAndPrepareNew(createGuid());

        // @ts-ignore
        this._drawingFinished.raiseEvent(this._measurements.length - 1);
    }

    canvasClickEvent(event: MouseEvent): void {
        const drawing = this._polygonDrawingHandler;

        if (drawing.mode === DrawingMode.AfterDraw) {
            drawing.prepareNewPrimitives();
        }

        const position = drawing.handleClick(event.pos);

        if (position) {
            this._label.show = true;
            drawing.polygon.show = true;
            drawing.polyline.show = true;
        }

        if (position && this._polygonDrawingHandler.polygon.positions.length === 1) {
            this._drawingStarted.raiseEvent();
        }
    }

    canvasMoveEvent(event: MouseEvent): void {
        if (event.button !== undefined) {
            return;
        }

        const nextPos = this._polygonDrawingHandler.handleMouseMove(event.pos);

        if (!nextPos) {
            return;
        }

        const positions = this._polygonDrawingHandler.positions.slice();

        positions.push(nextPos);
        this.updateArea(positions);
    }

    reset() {
        this._polygonDrawingHandler.prepareNewPrimitives();
        this._point.show = false;
    }

    // eslint-disable-next-line class-methods-use-this
    isDestroyed() {
        return false;
    }

    destroy() {
        this._polygonDrawingHandler.destroy();
        this._removeEvent();

        return destroyObject(this);
    }

    loadData(data: PositionData[], id: string) {
        const positions = newCartesian3ArrayFromArray(data);

        const drawingHandler = this._polygonDrawingHandler;

        drawingHandler.polyline.positions = positions;
        drawingHandler.polygon.positions = positions;
        this._label.show = true;

        this.updateArea(positions);

        for (let i = 0; i < positions.length; i++) {
            const point = drawingHandler.pointCollection.add(drawingHandler.pointOptions);

            point.position = positions[i];
            point.show = drawingHandler.showVertices;
            drawingHandler.points.push(point);
        }

        const scene = this._scene;

        this._label.position = calculateMostTopPosition(scene, positions, new Cartesian3());
        this._label.show = true;
        drawingHandler.polyline.show = true;
        drawingHandler.polygon.show = true;

        this.saveCurrentMeasurementAndPrepareNew(id);
    }

    saveCurrentMeasurementAndPrepareNew(id: string) {
        // we save current measurement and prepare new fresh

        const drawingHandler = this._polygonDrawingHandler;

        const polyline = drawingHandler.polyline;
        const polygon = drawingHandler.polygon;

        polyline.allowPicking = true;
        polygon.allowPicking = true;

        this._measurements.push(
            new AreaMeasurement({
                id: id,
                label: this._label,
                polygon: new EditablePolygon({
                    billboardCollection: this._billboardCollection,
                    pointPrimitiveCollection: drawingHandler.pointCollection,
                    pointOptions: drawingHandler.pointOptions,
                    points: drawingHandler.points,
                    polyline: polyline,
                    polygon: polygon,
                    showVertices: drawingHandler.showVertices
                }),
                measurementTool: this
            })
        );

        this._label = this._labelCollection.add(PrimitiveSettings.getLabelOptions());
        drawingHandler.prepareNewPrimitives();
    }

    removeMeasurementById(id: string) {
        const measurementResult = this._measurements;

        const index = measurementResult.findIndex((measurement) => measurement.id === id);

        if (index === -1) {
            return false;
        }

        const foundMeasurement = measurementResult[index] as AreaMeasurement;

        this._labelCollection.remove(foundMeasurement.labelPrimitive);
        this._primitives.remove(foundMeasurement.polygonPrimitive);
        this._primitives.remove(foundMeasurement.polylinePrimitive);

        const points = foundMeasurement.pointPrimitives;

        for (let i = 0; i < points.length; i++) {
            this._polygonDrawingHandler.pointCollection.remove(points[i]);
        }

        measurementResult.splice(index, 1);

        return true;
    }
}
